import { ButtonType, RelayPageDataTable, EpicIcons } from "@constellation-academy/epic-ui";
import { useState } from "react";
import { readInlineData, useMutation, usePaginationFragment } from "react-relay";
import { Button, ButtonVariant } from "@components/button";
import { DateTimeDisplay } from "@components/datetime-display";
import { IdDisplayField } from "@components/id-display-field";
import { useTranslation } from "@i18n/hooks";
import { manualOrdersTable_DeleteManualOrderMutation } from "@relay/manualOrdersTable_DeleteManualOrderMutation.graphql";
import { manualOrdersTable_ManualOrderFragment$key } from "@relay/manualOrdersTable_ManualOrderFragment.graphql";
import { manualOrdersTable_QueryFragment$key } from "@relay/manualOrdersTable_QueryFragment.graphql";
import { manualOrdersTable_Refetch } from "@relay/manualOrdersTable_Refetch.graphql";
import { StyledDialog } from "@screens/account-edit/account-edit.styles";
import { H2Span, P2Span } from "@themes/font-tags";
import {
	DELETE_MANUAL_ORDER_MUTATION,
	MANUAL_ORDER_FRAGMENT,
	QUERY_FRAGMENT,
} from "./manual-orders-table.graphql";
import { ManualOrdersTableProps } from "./manual-orders-table.types";
import { UpsertManualOrderModal } from "../upsert-manual-order-modal";

export const ManualOrdersTable = ({
	manualOrdersFragmentRef,
	accountId,
	canCreateManualOrders,
}: ManualOrdersTableProps) => {
	const { t } = useTranslation("common");
	const [addManualOrderDialogVisible, setAddManualOrderDialogVisible] = useState(false);

	const { data } = usePaginationFragment<
		manualOrdersTable_Refetch,
		manualOrdersTable_QueryFragment$key
	>(QUERY_FRAGMENT, manualOrdersFragmentRef);

	const [deleteManualOrder] = useMutation<manualOrdersTable_DeleteManualOrderMutation>(
		DELETE_MANUAL_ORDER_MUTATION,
	);

	const manualOrders = data.Admin.Billing.GetManualOrdersForAccount.edges?.map((edge) =>
		readInlineData<manualOrdersTable_ManualOrderFragment$key>(
			MANUAL_ORDER_FRAGMENT,
			edge?.node!,
		),
	);

	const createDeleteManualOrderHandler = (id: string) => () => {
		deleteManualOrder({
			variables: {
				input: { id },
				connections: [data.Admin.Billing.GetManualOrdersForAccount.__id],
			},
		});
	};

	const handleAddManualOrderOnClick = () => {
		setAddManualOrderDialogVisible(true);
	};

	const handleAddManualOrderDialogOnHide = () => {
		setAddManualOrderDialogVisible(false);
	};

	const handleAddManualOrderDialogOnCreated = () => {
		setAddManualOrderDialogVisible(false);
	};

	return (
		<div>
			<RelayPageDataTable<
				manualOrdersTable_Refetch,
				manualOrdersTable_QueryFragment$key,
				manualOrdersTable_ManualOrderFragment$key
			>
				queryFragment={QUERY_FRAGMENT}
				parentFragmentRef={manualOrdersFragmentRef}
				nodeInlineFragment={MANUAL_ORDER_FRAGMENT}
				extractConnection={(data) => data.Admin.Billing.GetManualOrdersForAccount}
				value={manualOrders}
				header={
					canCreateManualOrders && (
						<Button
							type={ButtonType.Button}
							variant={ButtonVariant.Strong}
							icon={EpicIcons.PLUS}
							label={t("order.addManualOrder")}
							onClick={handleAddManualOrderOnClick}
						/>
					)
				}
			>
				{(Column) => (
					<>
						<Column
							header={t("table.id")}
							body={(item) => <IdDisplayField id={item.id} />}
						/>
						<Column
							header={t("order.invoiceNumber")}
							body={(item) => item.invoiceNumber}
						/>
						<Column header={t("order.seller")} body={(item) => item.salesPerson.name} />
						<Column
							header={t("table.createdAt")}
							body={(item) => <DateTimeDisplay value={item.createdAt} />}
						/>
						<Column
							header={t("order.products")}
							body={(item) => (
								<>
									{item.productSelection.map((product) => (
										<div key={product.product.id}>
											<H2Span>{product.product.title}</H2Span>
											<div>
												<P2Span>{t("order.netPrice")}: </P2Span>
												{product.price.netPrice}€
											</div>
											<div>
												<P2Span>{t("order.grossPrice")}: </P2Span>
												{product.price.grossPrice}€
											</div>
											<div>
												<P2Span>{t("order.taxRate")}: </P2Span>
												{product.price.taxRatePercentage}%
											</div>
										</div>
									))}
								</>
							)}
						/>
						<Column
							header={t("order.discountCodes")}
							body={(item) =>
								item.discountSelection.map((discount) => discount.code).join(", ")
							}
						/>
						<Column
							header={t("table.actions")}
							body={(item) => (
								<>
									<Button
										type={ButtonType.Button}
										variant={ButtonVariant.Error}
										icon={EpicIcons.TRASH}
										tooltip={t("order.deleteTooltip")}
										onClick={createDeleteManualOrderHandler(item.id)}
									/>
								</>
							)}
							style={{ width: "10%" }}
						/>
					</>
				)}
			</RelayPageDataTable>
			<StyledDialog
				visible={addManualOrderDialogVisible}
				onHide={handleAddManualOrderDialogOnHide}
			>
				<UpsertManualOrderModal
					accountId={accountId}
					connections={[data.Admin.Billing.GetManualOrdersForAccount.__id]}
					onCreated={handleAddManualOrderDialogOnCreated}
				/>
			</StyledDialog>
		</div>
	);
};
